<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Customer"
        :baseView="baseView"
        :primaryLabel="model.Name"
        :secondaryLabel="baseView.id"
        recordType="Customer"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-list">
      <div class="data-grid-details">
        <GcDataGrid
          :grouping="true"
          :export="true"
          :summary="true"
          :dataSourceConfig="
            screenJson.gridData
              ? screenJson.gridData.gridOptions.dataSource
              : undefined
          "
          :rowClick="rowClickCallBack"
          :columns="getColumns"
          :valueChangeListners="getListners"
          :childGridColumns="getChildGridColumns"
          :gridToolbar="
            screenJson.gridData ? screenJson.gridData.toolbar : undefined
          "
          :padColumn="true"
          :id="'transaction-list-grid'"
          v-if="loaded && screenJson.gridData"
          ref="transactionListGrid"
          :isEditableGrid="true"
          :showFooter="false"
          :gridOptions="
            screenJson.gridData ? screenJson.gridData.gridOptions : undefined
          "
          :elementAttrClass="'transaction-template'"
          :paging="true"
          :heightPadding="20"
        />
      </div>
    </div>
  </gc-main-layout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import CustomerTabsMixin from "@/_mixins/customer-tabs.mixin";
import DataGridMixin from "../../_mixins/data-grid.mixin";
import { CustomerService } from "../../_services/api/customers/customer.service";

import { CommonStorage, FeatureStorage } from "../../_services/storage";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { CacheService } from "../../_services/cache/cache-service";
import { SalesOrderListService } from "../../_services/api/sales/sales-order-list.service";

function logDev() {
  // eslint-disable-next-line no-console
  console.log("CX Log >>> ", arguments);
}

export default {
  props: {
    /**
     * contains report configurations
     */
    reportConfig: {
      type: Object,
      default: undefined,
    },
  },
  mixins: [RouteMixin, CustomerTabsMixin, DataGridMixin],
  components: {},

  data() {
    return {
      persistenceKey: "customer-call-log_list",
      backButtonRoute: `/customer`,
      CustomerSalesRepresentativeCode: undefined,
      model: {
        Code: "",
        Name: "",
      },

      gridOptions: {
        noDataMessage: {
          addNew: {
            class: "no-data",
            title: "No Transactions",
            message:
              "Currently there are no call logs for this customer. Give them a call and sell them something today!",
          },
        },
      },
      loaded: false,
    };
  },
  computed: {
    defaultDateFilterValue() {
      let d = new Date();
      d.setMonth(d.getMonth() - 3);
      return d;
    },
    getCustomerRepDefault() {
      if (this.CustomerSalesRepresentativeCode)
        return [this.CustomerSalesRepresentativeCode];
      else return false;
    },
    getChildGridColumns() {
      return this.screenJson.gridData.childColumns;
    },
    getListners() {
      return this.screenJson.gridData
        ? this.screenJson.gridData.valueChangedEventListners
        : undefined;
    },
    screenJson() {
      var config = {
        title: "Customers",

        exportFileName: "Customers Call Logs",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "CallLogList",
              filter: [["CustomerCode", "=", this.baseView.id]],
              serverPaging: true,
              allowGrouping: false,
              select: ["CallLogRecordID"].concat(
                this.getSelectFields(this.getColumns)
              ),
              postProcessCallback: this.callLogListPostProcessCallback,
            },

            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Call Logs",
                message: "Get Started, Add a Call Log",
              },
            },
          },

          toolbar: {
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/customer-call-logs/create",
                clickAction: () => {
                  this.goToCreateCallLogs();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission("CallLogs", "create"),
              },
            ],
            filterOnEnter: true,
          },
          columns: this.getColumns,
        },
      };

      return config;
    },

    getColumns() {
      return [
        {
          screenField: "Customer",
          sortIndex: 0,
          cellTemplateRecord: {
            primary: [
              {
                dataField: "CustomerName",
              },
            ],
            secondary: [
              {
                dataField: "CustomerCode",
              },
            ],
          },
          exportFields: [
            {
              dataField: "CustomerCode",
              label: "Customer Code",
            },
            {
              dataField: "CustomerName",
              label: "Customer Name",
            },
          ],
          fixed: false,
          fixedPosition: "left",
          allowHiding: false,
        },
        {
          screenField: "CustomerName",
          gridLabel: "CustomerNameFilter",
          dataField: "CustomerName",
          visible: false,
          showInColumnChooser: false,
          dataType: "string",
          type: "Text",
          filterDataField: "CustomerSearchText",
        },
        {
          screenField: "CallType",
          gridDataField: "CallType",
          label: "Type",
          dataField: "CallType",
          cellTemplateRecord: {
            primary: [
              {
                dataField: "CallTypeDescription",
              },
            ],
            secondary: [
              {
                dataField: "CallType",
              },
            ],
          },
          exportFields: [
            {
              dataField: "CallType",
              label: "Call Type Code",
            },
            {
              dataField: "CallTypeDescription",
              label: "Call Type Description",
            },
          ],
          filterDataField: "CallType",
          filterIndex: 3,
          maxWidth: ColumnWidth.CustomerType.width,
        },
        {
          screenField: "Date",
          sortOrder: "desc",
          dataField: "CallDate",
          filterDataField: "CallDate",
          filterIndex: 2,
          filterOperator: ">",
          filterDefaultValue: this.defaultDateFilterValue,
          label: "Start Date",
          filterLabel: "Start Date",
        },
        {
          screenField: "CallStartTime",
          label: "Start Time",
          filterDataField: "CallStartTime",
          dataField: "CallStartTime",
          type: "Time",
          maxWidth: ColumnWidth.Time.width,
        },
        {
          screenField: "CallEndTime",
          label: "End Time",
          dataField: "CallEndTime",
          filterDataField: "CallEndTime",
          type: "Time",
          maxWidth: ColumnWidth.Time.width,
        },
        {
          screenField: "CallDuration",
          label: "Duration (mins)",
          dataField: "CallDuration",
          filterDataField: "CallDuration",
          maxWidth: ColumnWidth.Duration.width,
        },
        {
          screenField: "NoteCount",
          label: "# Notes",
          dataField: "NoteCount",
          filterDataField: "NoteCount",
          maxWidth: ColumnWidth.Duration.width,
        },
        {
          screenField: "NoteImagesMetaCount",
          label: "# Media",
          dataField: "NoteImagesMetaCount",
          filterDataField: "NoteImagesMetaCount",
          maxWidth: ColumnWidth.Duration.width,
        },
        {
          screenField: "SalesRepresentative",
          label: "Sales Rep",
          dataField: "CustomerSalesRepresentativeCode",
          filterLabel: "Sales Rep",
          filterDataField: "CustomerSalesRepresentativeCode",
          filterIndex: 1,
          visible: false,
          showInColumnChooser: false,
          filterDefaultValue: this.getCustomerRepDefault,
        },
        {
          screenField: "ChangedBy",
          label: "Changed By",
          dataField: "ChangedBy",
          filterLabel: "Changed By",
          filterDataField: "ChangedBy",
          filterIndex: 4,
          maxWidth: ColumnWidth.RepCode.width,
          dataType: "string",
          type: "Text",
        },
        {
          screenField: "OrderStatus",
          gridDataField: "Status",
          gridLabel: "Status",
          dataField: "Status",
          filterDataField: "Status",
          filterIndex: 4,
          dataSourceConfig: {
            data: [
              { key: "A", value: "Active" },
              { key: "C", value: "Complete" },
              { key: "P", value: "Pending" },
              { key: "X", value: "Cancelled" },
            ],
          },
          valueExpr: "key",
          displayExpr: "value",
          filterDefaultValue: ["A", "C"],
        },
        {
          screenField: "GridRecordEllipsisMenu",
          select: false,
          allowSearch: false,
          cssClass: "padding-7",
          ellipsisMenu: {
            itemAction: (data) => {
              var items = [
                {
                  menuText: "Customer List",
                  data: data,
                  clickAction: this.navigateToDestination,
                  disabled: false,
                },
              ];

              return items;
            },
            keyExpr: "menuText",
            displayExpr: "menuText",
          },
          // eslint-disable-next-line no-unused-vars
          makeDisabled: (data) => {
            // if (data) return data.data.Status != "C";
            return true;
          },
        },
      ];
    },
  },
  mounted() {
    logDev("mounted ", this.columns);
  },

  async created() {
    CommonStorage.clear("go_back_route");
    this.selectedIndex = this.tabs.findIndex((x) => x.text === "Call Logs");

    await CustomerService.getByCode(this.baseView.id, ["Code", "Name"]).then(
      async (response) => {
        if (response.data.value.length > 0) {
          this.model.Code = response.data.value[0].Code;
          this.model.Name = response.data.value[0].Name;
        }
      }
    );

    await this.setDefaultCustomerRep();

    this.loaded = true;
  },
  methods: {
    goToCreateCallLogs() {
      this.$router.push({
        path: `/call-logs/create`,
        query: {
          cus_id: this.baseView.id,
          from: "/customer-call-logs/" + this.baseView.id,
        },
      });
    },
    callLogListPostProcessCallback(data) {
      data.forEach((element) => {
        if (element.CallDate)
          element.CallDate = element.CallDate.dateWithOffset();

        if (element.CallStartTime)
          element.CallStartTime = element.CallStartTime.dateWithOffset();

        if (element.CallEndTime)
          element.CallEndTime = element.CallEndTime.dateWithOffset();
      });
      return data;
    },
    async setDefaultCustomerRep() {
      var defaultSalesRep = CacheService.getCache(
        "UserDefaultSalesRepresentative"
      );

      if (!defaultSalesRep) {
        await SalesOrderListService.getUserDefaultSalesRepresentative()
          .then((result) => {
            logDev(
              "SalesOrderListService.getUserDefaultSalesRepresentative",
              result
            );
            this.setLoading = false;
            if (result.data.value[0].SalesRepresentativeCode != "") {
              this.CustomerSalesRepresentativeCode =
                result.data.value[0].SalesRepresentativeCode;
            }

            CacheService.saveCache(
              "UserDefaultSalesRepresentative",
              result.data.value[0],
              2
            );
            this.loaded = true;
          })
          .catch((err) => {
            logDev(
              "Error in SalesOrderListService.getUserDefaultSalesRepresentative",
              err
            );

            if (err) {
              this.setLoading = false;
              this.loaded = true;
            }
          });
      } else {
        if (defaultSalesRep.SalesRepresentativeCode != "")
          this.CustomerSalesRepresentativeCode =
            defaultSalesRep.SalesRepresentativeCode;

        this.loaded = true;
      }
    },
    backButtonFunction() {
      this.$router.push({
        path: this.backButtonRoute,
      });
    },
    // eslint-disable-next-line no-unused-vars
    rowClickCallBack(e) {
      if (
        e.rowType == "data" &&
        e.column.screenField != "Status" &&
        e.column.screenField != "GridRecordEllipsisMenu"
      ) {
        this.onRowClicked(e, {
          view: this,
          page: "call-logs",
          mode: "review",
          idProperty: "CallLogRecordID",
          query: {
            cus_id: e.data.CustomerCode,
            from: "customer-call-logs",
          },
        });

        // this.onRowClicked(e, {
        //   view: this,
        //   page: "customer",
        //   mode: "review",
        //   idProperty: "Code",
        // });
      }
    },
  },
};
</script>
